/**
 * Computa scores y moduleAggregates a partir de las respuestas guardadas.
 * Centraliza la lógica de scoring para ResultDetailPage y PDF.
 */

import { getCommercialName } from '@/config/batteries';
import { PCL_10_ITEMS, EEL_12_ITEMS, APO_6_ITEMS, MINI_IPIP_20_ITEMS } from '@/config/instruments';
import { MOCK_ITEMS, MOCK_TEST_ID } from '@/config/testItems';
import {
  scorePCL10,
  getPCL10Band,
  getPCL10BandFromZ,
  getPCL10RhSummary,
  buildRFBAggregateForPersistence,
  scoreRA10,
  getRA10Band,
  getRA10BandFromZ,
  getRA10RhSummary,
  scoreEEL12,
  getEEL12Band,
  getEEL12BandFromZ,
  getEEL12RhSummary,
  computeEEL12Consistency,
  scoreJKOfficeAdmin,
  scoreJKSalesRetail,
  scoreAPO6,
  scoreML8,
  scoreMiniIPIP20WithNorm,
} from '@/lib/instrumentScoring';
import { getMiniIpipNorm } from '@/lib/miniIpipNorms';
import { scoreMockPersonality, getBand } from '@/lib/scoring';
import type { RFBOptionLetter, RA10OptionLetter, ML8OptionLetter } from '@/config/instruments';
import { getScalarNorm, computeZScore } from '@/lib/scalarNorms';

export interface ComputedScore {
  testId: string;
  commercialName?: string;
  rawScore?: number;
  scaleScore?: number;
  band?: string;
  bandLabel?: string;
  rhSummary?: string;
  normScopeId?: string;
  nNorm?: number;
  zScore?: number;
}

export interface MiniIpipNormContext {
  companyId: string;
  industryKey?: string | null;
  countryCode?: string | null;
}

export interface ComputeResultsInput {
  answers: Record<string, unknown>;
  /** Para MINI_IPIP_20: obtiene norma y clasifica por z-score. Si falta, usa fallback. */
  normContext?: MiniIpipNormContext;
}

export interface ComputeResultsOutput {
  scores: ComputedScore[];
  moduleAggregates: Record<string, unknown>;
}

function likertToOrderedArray(
  answers: Record<string, number>,
  itemIds: string[]
): number[] {
  return itemIds.map((id) => answers[id] ?? 0);
}

export async function computeResults(input: ComputeResultsInput): Promise<ComputeResultsOutput> {
  const { answers, normContext } = input;
  const scores: ComputedScore[] = [];
  const moduleAggregates: Record<string, unknown> = {};

  // ─── Perfil personalidad (mock) ───
  const perfilAnswers = answers.perfil_personalidad as Record<string, number> | undefined;
  if (perfilAnswers && Object.keys(perfilAnswers).length > 0) {
    const rawScore = scoreMockPersonality(perfilAnswers);
    const band = getBand(rawScore, 3, 15);
    scores.push({
      testId: 'perfil_personalidad',
      commercialName: getCommercialName('perfil_personalidad'),
      rawScore,
      band,
      bandLabel: band === 'low' ? 'Bajo' : band === 'high' ? 'Alto' : 'Medio',
    });
  }

  // Legacy: mock-personality
  const mockAnswers = answers[MOCK_TEST_ID] as Record<string, number> | undefined;
  if (mockAnswers && Object.keys(mockAnswers).length > 0) {
    const rawScore = scoreMockPersonality(mockAnswers);
    const band = getBand(rawScore, 3, 15);
    scores.push({
      testId: MOCK_TEST_ID,
      commercialName: getCommercialName(MOCK_TEST_ID),
      rawScore,
      band,
      bandLabel: band === 'low' ? 'Bajo' : band === 'high' ? 'Alto' : 'Medio',
    });
  }

  // ─── PCL_10 ───
  const pclAnswers = answers.PCL_10 as Record<string, number> | undefined;
  if (pclAnswers) {
    const ordered = likertToOrderedArray(pclAnswers, PCL_10_ITEMS.map((i) => i.id));
    const total = scorePCL10(ordered);
    const pclPercent = Math.round((total / 50) * 100);
    // Fallback por defecto: bandas clásicas
    let band = getPCL10Band(total);
    let normScopeId: string | undefined;
    let nNorm: number | undefined;
    let zScore: number | undefined;

    if (normContext) {
      try {
        const norm = await getScalarNorm({
          testId: 'PCL_10',
          companyId: normContext.companyId,
          industryKey: normContext.industryKey,
          countryCode: normContext.countryCode,
        });
        if (norm) {
          const z = computeZScore(pclPercent, norm.mean, norm.sd);
          if (z != null) {
            band = { key: getPCL10BandFromZ(z), label: getPCL10Band(total).label };
            normScopeId = norm.scopeId;
            nNorm = norm.n;
            zScore = z;
          }
        }
      } catch {
        // En caso de error de red u otro, se mantiene el fallback de banda fija
      }
    }
    scores.push({
      testId: 'PCL_10',
      commercialName: getCommercialName('PCL_10'),
      rawScore: total,
      scaleScore: total,
      scorePercent: pclPercent,
      band: band.key,
      bandLabel: band.label,
      rhSummary: getPCL10RhSummary(band.key),
      ...(normScopeId && { normScopeId }),
      ...(nNorm != null && { nNorm }),
      ...(zScore != null && { zScore }),
    });
    moduleAggregates.PCL_10 = {
      totalScore: total,
      answersRaw: ordered,
      ...(normScopeId && { normScopeId }),
      ...(nNorm != null && { nNorm }),
      ...(zScore != null && { zScore }),
    };
  }

  // ─── EEL_12 ───
  const eelAnswers = answers.EEL_12 as Record<string, number> | undefined;
  if (eelAnswers) {
    const ordered = likertToOrderedArray(eelAnswers, EEL_12_ITEMS.map((i) => i.id));
    const { scoreRaw, score50, itemScores } = scoreEEL12(ordered);
    // Fallback inicial: bandas por score50
    let band = getEEL12Band(score50);
    const consistency = computeEEL12Consistency(ordered);
    const eelPercent = Math.round((score50 / 50) * 100);
    let normScopeId: string | undefined;
    let nNorm: number | undefined;
    let zScore: number | undefined;

    if (normContext) {
      try {
        const norm = await getScalarNorm({
          testId: 'EEL_12',
          companyId: normContext.companyId,
          industryKey: normContext.industryKey,
          countryCode: normContext.countryCode,
        });
        if (norm) {
          const z = computeZScore(eelPercent, norm.mean, norm.sd);
          if (z != null) {
            const zBandKey = getEEL12BandFromZ(z);
            band = { key: zBandKey, label: getEEL12Band(score50).label };
            normScopeId = norm.scopeId;
            nNorm = norm.n;
            zScore = z;
          }
        }
      } catch {
        // Fallback a bandas nativas
      }
    }
    scores.push({
      testId: 'EEL_12',
      commercialName: getCommercialName('EEL_12'),
      rawScore: scoreRaw,
      scaleScore: score50,
      scorePercent: eelPercent,
      band: band.key,
      bandLabel: band.label,
      rhSummary: getEEL12RhSummary(band.key),
      ...(normScopeId && { normScopeId }),
      ...(nNorm != null && { nNorm }),
      ...(zScore != null && { zScore }),
    });
    moduleAggregates.EEL_12 = {
      scoreRaw,
      score50,
      itemScores,
      bandKey: band.key,
      bandLabel: band.label,
      adequacyLabel: band.label,
      interpretationText: getEEL12RhSummary(band.key),
      consistencyLabel: consistency.consistencyLabel,
      ...(consistency.consistencyNote != null && { consistencyNote: consistency.consistencyNote }),
      ...(normScopeId && { normScopeId }),
      ...(nNorm != null && { nNorm }),
      ...(zScore != null && { zScore }),
    };
  }

  // ─── RA_10 (test seleccionable: estilo_trabajo_organizacion; derivado: responsabilidad_control) ───
  const raChoices = answers.RA_10 as RA10OptionLetter[] | undefined;
  if (raChoices && raChoices.length >= 10) {
    const { score20, score50, itemScores } = scoreRA10(raChoices.slice(0, 10));
    // Fallback inicial: bandas por score20
    let band = getRA10Band(score20);
    const raPercent = Math.round((score50 / 50) * 100);
    let normScopeId: string | undefined;
    let nNorm: number | undefined;
    let zScore: number | undefined;

    if (normContext) {
      try {
        const norm = await getScalarNorm({
          testId: 'RA_10',
          companyId: normContext.companyId,
          industryKey: normContext.industryKey,
          countryCode: normContext.countryCode,
        });
        if (norm) {
          const z = computeZScore(raPercent, norm.mean, norm.sd);
          if (z != null) {
            const zBandKey = getRA10BandFromZ(z);
            band = { key: zBandKey, label: getRA10Band(score20).label };
            normScopeId = norm.scopeId;
            nNorm = norm.n;
            zScore = z;
          }
        }
      } catch {
        // Fallback a bandas nativas
      }
    }
    scores.push({
      testId: 'RA_10',
      commercialName: getCommercialName('estilo_trabajo_organizacion'),
      rawScore: score20,
      scaleScore: score50,
      scorePercent: raPercent,
      band: band.key,
      bandLabel: band.label,
      rhSummary: getRA10RhSummary(band.key),
      ...(normScopeId && { normScopeId }),
      ...(nNorm != null && { nNorm }),
      ...(zScore != null && { zScore }),
    });
    const percent = Math.round((score50 / 50) * 100);
    moduleAggregates.RA_10 = {
      score20,
      score50,
      itemScores,
      bandKey: band.key,
      bandLabel: band.label,
      derived: {
        responsabilidadControl: {
          derived: true,
          sourceInstrumentId: 'RA_10',
          percent,
          adecuacion: band.label,
          text: getRA10RhSummary(band.key),
        },
      },
      ...(normScopeId && { normScopeId }),
      ...(nNorm != null && { nNorm }),
      ...(zScore != null && { zScore }),
    };
  }

  // ─── RFB_SJ_8 ───
  const rfbData = answers.RFB_SJ_8 as { firstChoices?: RFBOptionLetter[]; avoidChoices?: RFBOptionLetter[] } | undefined;
  if (rfbData?.firstChoices && rfbData?.avoidChoices && rfbData.firstChoices.length >= 8 && rfbData.avoidChoices.length >= 8) {
    const agg = buildRFBAggregateForPersistence(
      rfbData.firstChoices.slice(0, 8),
      rfbData.avoidChoices.slice(0, 8)
    );
    const rfbTotal = agg.totalScore as number;
    const rfbPercent = Math.round(Math.max(0, Math.min(100, ((rfbTotal + 8) / 24) * 100)));
    scores.push({
      testId: 'RFB_SJ_8',
      commercialName: getCommercialName('RFB_SJ_8'),
      rawScore: rfbTotal,
      scaleScore: rfbTotal,
      scorePercent: rfbPercent,
      band: agg.bandKey as string,
      bandLabel: agg.bandLabel as string,
      rhSummary: agg.rhSummary as string,
    });
    moduleAggregates.RFB_SJ_8 = agg;
  }

  // ─── JK_OFFICE_ADMIN ───
  const jkOaData = answers.JK_OFFICE_ADMIN as {
    answers?: Record<string, 'V' | 'F' | 'N'>;
    itemsPresented?: { id: string; correctAnswer: 'V' | 'F' }[];
  } | undefined;
  if (jkOaData?.answers && jkOaData?.itemsPresented) {
    const result = scoreJKOfficeAdmin(jkOaData.itemsPresented, jkOaData.answers);
    scores.push({
      testId: 'JK_OFFICE_ADMIN',
      commercialName: getCommercialName('JK_OFFICE_ADMIN'),
      rawScore: result.correctCount,
      scaleScore: result.scorePercent,
      scorePercent: result.scorePercent,
      band: result.bandKey,
      bandLabel: result.bandLabel,
    });
    moduleAggregates.JK_OFFICE_ADMIN = {
      correctCount: result.correctCount,
      scorePercent: result.scorePercent,
      answeredNoSeCount: result.answeredNoSeCount,
      bandKey: result.bandKey,
      bandLabel: result.bandLabel,
      answersRaw: jkOaData.answers,
    };
  }

  // ─── JK_SALES_RETAIL ───
  const jkSrData = answers.JK_SALES_RETAIL as {
    answers?: Record<string, 'V' | 'F' | 'N'>;
    itemsPresented?: { id: string; correctAnswer: 'V' | 'F' }[];
  } | undefined;
  if (jkSrData?.answers && jkSrData?.itemsPresented) {
    const result = scoreJKSalesRetail(jkSrData.itemsPresented, jkSrData.answers);
    scores.push({
      testId: 'JK_SALES_RETAIL',
      commercialName: getCommercialName('conocimiento_puesto_ventas_retail'),
      rawScore: result.correctCount,
      scaleScore: result.scorePercent,
      scorePercent: result.scorePercent,
      band: result.bandKey,
      bandLabel: result.bandLabel,
    });
    moduleAggregates.JK_SALES_RETAIL = {
      correctCount: result.correctCount,
      scorePercent: result.scorePercent,
      answeredNoSeCount: result.answeredNoSeCount,
      bandKey: result.bandKey,
      bandLabel: result.bandLabel,
      answersRaw: jkSrData.answers,
    };
  }

  // ─── APO_6 ───
  const apoChoices = answers.APO_6 as (string | undefined)[] | undefined;
  if (apoChoices && apoChoices.length >= 6) {
    const correctAnswers = APO_6_ITEMS.map((i) => i.correctAnswer);
    const result = scoreAPO6(apoChoices.slice(0, 6), correctAnswers);
    scores.push({
      testId: 'APO_6',
      commercialName: getCommercialName('APO_6'),
      rawScore: result.correctCount,
      scaleScore: result.percent,
      scorePercent: result.percent,
      band: result.adecuacion,
      bandLabel: result.adecuacionLabel,
    });
    moduleAggregates.APO_6 = {
      answers: apoChoices.slice(0, 6),
      correctCount: result.correctCount,
      percent: result.percent,
      adecuacion: result.adecuacion,
      adecuacionLabel: result.adecuacionLabel,
    };
  }

  // ─── ML_8 ───
  const mlChoices = answers.ML_8 as ML8OptionLetter[] | undefined;
  if (mlChoices && mlChoices.length >= 8) {
    const result = scoreML8(mlChoices.slice(0, 8));
    scores.push({
      testId: 'ML_8',
      commercialName: getCommercialName('ML_8'),
    });
    moduleAggregates.ML_8 = {
      rawAnswers: result.rawAnswers,
      counts: result.counts,
      labels: result.labels,
      derived: result.derived,
    };
  }

  // ─── Mini-IPIP (20) — Perfil Integral de Personalidad Profesional ───
  const miniIPIPAnswers = answers.MINI_IPIP_20 as Record<string, number> | undefined;
  if (miniIPIPAnswers && Object.keys(miniIPIPAnswers).length >= 20) {
    const ordered = likertToOrderedArray(miniIPIPAnswers, MINI_IPIP_20_ITEMS.map((i) => i.id));
    let norm = null;
    if (normContext) {
      norm = await getMiniIpipNorm({
        companyId: normContext.companyId,
        industryKey: normContext.industryKey,
        countryCode: normContext.countryCode,
      });
    }
    const result = scoreMiniIPIP20WithNorm(ordered, norm);
    scores.push({
      testId: 'MINI_IPIP_20',
      commercialName: getCommercialName('MINI_IPIP_20'),
    });
    moduleAggregates.MINI_IPIP_20 = {
      answersRaw: result.answersRaw,
      answersAdjusted: result.answersAdjusted,
      dimensions: result.dimensions,
    };
  }

  return { scores, moduleAggregates };
}
